import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { DollarSign, Users, Share2, TrendingUp, Copy, Check, RotateCw } from "lucide-react";
import { toast } from "sonner";

// Partner dashboard — shown to any authed user whose email is registered on a
// `partners` doc. Backend gates via /partner/dashboard (returns 403 otherwise).
export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["partner-dashboard"],
    queryFn: async () => (await api.get("/partner/dashboard")).data,
    retry: false,
  });

  const copyShare = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  if (isLoading) {
    return <div className="p-8 text-sm" style={{ color: "var(--fg-3)" }}>Loading dashboard…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen p-8 max-w-2xl mx-auto">
        <h1 className="font-heading text-3xl mb-4">Not a partner yet</h1>
        <p className="text-sm mb-6" style={{ color: "var(--fg-2)" }}>
          Your email isn&apos;t registered in the partner program.
          {" "}Learn more about how it works →
        </p>
        <button
          onClick={() => navigate("/partners")}
          className="px-5 py-2.5 text-xs font-semibold tracking-wide"
          style={{ background: "var(--accent)", color: "white", borderRadius: "10px" }}
          data-testid="partner-apply-link"
        >
          APPLY TO BE A PARTNER
        </button>
      </div>
    );
  }

  const { partner, stats, recent_commissions } = data;

  return (
    <div className="min-h-screen p-8 max-w-5xl mx-auto">
      <div className="label-eyebrow mb-2" style={{ color: "var(--accent)" }}>PARTNER DASHBOARD</div>
      <div className="flex items-baseline gap-3 mb-8 flex-wrap">
        <h1 className="font-heading text-4xl font-semibold tracking-tight">{partner.name}</h1>
        <span
          className="font-mono-num px-2 py-1 text-[11px] tracking-widest"
          style={{ background: "rgba(168,85,247,0.14)", color: "var(--accent)", borderRadius: "6px" }}
          data-testid="partner-code"
        >
          {partner.code}
        </span>
      </div>

      {/* Share link */}
      <div
        className="p-5 mb-8 flex items-center gap-4"
        style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}
      >
        <Share2 className="h-5 w-5 shrink-0" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>YOUR REFERRAL LINK</div>
          <div className="mt-1 font-mono-num text-sm break-all" style={{ color: "var(--fg)" }} data-testid="partner-share-url">
            {data.share_url}
          </div>
        </div>
        <button
          onClick={copyShare}
          data-testid="partner-copy-share"
          className="shrink-0 px-3 py-2 text-xs font-semibold flex items-center gap-1.5"
          style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "COPIED" : "COPY"}
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="LIFETIME REFERRALS" value={stats.signup_count} icon={Users} testid="stat-signups" />
        <StatCard label="ACTIVE SUBSCRIBERS" value={stats.active_subscribers} icon={TrendingUp} testid="stat-active" accent="green" />
        <StatCard label="THIS MONTH" value={`$${stats.commission_mtd.toFixed(2)}`} icon={DollarSign} testid="stat-mtd" accent="green" />
        <StatCard label="PENDING PAYOUT" value={`$${stats.commission_pending_payout.toFixed(2)}`} icon={RotateCw} testid="stat-pending" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Deal summary */}
        <div className="p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
          <div className="label-eyebrow mb-3" style={{ color: "var(--fg-3)" }}>YOUR DEAL</div>
          <div className="space-y-2 text-sm">
            <Row label="Customer discount" value={`${partner.discount_pct}% off × ${partner.discount_months} months`} />
            <Row label="Your commission" value={`${partner.commission_pct}% recurring`} valueColor="var(--green)" />
            <Row label="Lifetime earned" value={`$${stats.commission_lifetime.toFixed(2)}`} valueColor="var(--green)" />
          </div>
        </div>

        {/* Recent commission events */}
        <div className="md:col-span-2 p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
          <div className="label-eyebrow mb-3" style={{ color: "var(--fg-3)" }}>RECENT COMMISSIONS ({recent_commissions.length})</div>
          {recent_commissions.length === 0 ? (
            <div className="text-xs" style={{ color: "var(--fg-3)" }}>
              No commission events yet. Share your link + wait for your first referred subscription to bill.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {recent_commissions.map((c) => (
                <div key={c.id || c.invoice_id} className="flex items-center gap-3 py-1.5 px-2 text-xs font-mono-num" style={{ borderBottom: "1px solid var(--border)" }}>
                  <span style={{ color: "var(--fg-3)", minWidth: 90 }}>{new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  <span style={{ color: "var(--fg-3)", flex: 1 }}>${c.amount_paid_usd.toFixed(2)} paid · {c.commission_pct}%</span>
                  <span style={{ color: "var(--green)" }}>+${c.commission_amount.toFixed(2)}</span>
                  {c.paid_out ? (
                    <span className="text-[10px] tracking-wider" style={{ color: "var(--fg-3)" }}>PAID</span>
                  ) : (
                    <span className="text-[10px] tracking-wider" style={{ color: "var(--accent)" }}>PENDING</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, testid, accent }) => (
  <div
    className="p-5"
    style={{
      background: "var(--surface)",
      border: `1px solid ${accent === "green" ? "rgba(34,197,94,0.28)" : "var(--border)"}`,
      borderRadius: "12px",
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} style={{ color: accent === "green" ? "var(--green)" : "var(--fg-3)" }} />
      <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>{label}</div>
    </div>
    <div className="font-mono-num text-2xl" style={{ color: accent === "green" ? "var(--green)" : "var(--fg)" }}>{value}</div>
  </div>
);

const Row = ({ label, value, valueColor }) => (
  <div className="flex justify-between text-xs">
    <span style={{ color: "var(--fg-3)" }}>{label}</span>
    <span className="font-mono-num" style={{ color: valueColor || "var(--fg)" }}>{value}</span>
  </div>
);
